import React, { Suspense, lazy, useEffect, useState } from 'react';
import { KYCWizardProvider, useKYCWizard, KYC_STEPS } from './KYCWizardContext';
import KYCProgressIndicator from './KYCProgressIndicator';
import KYCStepNavigation from './KYCStepNavigation';
import { X, Save, Clock, Loader2, CheckCircle2, ChevronLeft, ChevronRight, ChevronsUpDown, Check } from 'lucide-react';
import { format } from 'date-fns';
import kycService from '../../services/kycService';
import { toast } from 'sonner';
import type { KYCReviewComment } from '../../types/complianceWorkflow';

// TASK-373: Lazy load step components — DocumentUpload removed (now in KYCWorkspace)
const PersonalInfoStep = lazy(() => import('./steps/PersonalInfoStep'));
const ContactInfoStep = lazy(() => import('./steps/ContactInfoStep'));
const EmploymentInfoStep = lazy(() => import('./steps/EmploymentInfoStep'));
const PersonalBackgroundStep = lazy(() => import('./steps/PersonalBackgroundStep'));
const TaxInfoStep = lazy(() => import('./steps/TaxInfoStep'));
const AdverseMediaStep = lazy(() => import('./steps/AdverseMediaStep'));
const BusinessActivitiesStep = lazy(() => import('./steps/BusinessActivitiesStep'));
const WealthSourceStep = lazy(() => import('./steps/WealthSourceStep'));
const IncomeSourceStep = lazy(() => import('./steps/IncomeSourceStep'));
const ReviewSubmitStep = lazy(() => import('./steps/ReviewSubmitStep'));

interface KYCWizardProps {
  clientId: string;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  onClose?: () => void;
  onComplete?: (data: any) => void;
  /** External prefill data (e.g., from staging review) - skips automatic fetch */
  externalPrefillData?: any;
  /** Review mode - relaxes validation, allows submission with incomplete data */
  reviewMode?: boolean;
  /** TASK-373: Embedded mode — hides chrome (header, sidebar) for workspace use */
  embedded?: boolean;
  /** CO review comments for advisor view of changes_requested feedback */
  reviewComments?: KYCReviewComment[];
  /** True when profile is in changes_requested status */
  isChangesRequested?: boolean;
}

const KYCWizardContent: React.FC<{ clientName: string; onClose?: () => void; embedded?: boolean }> = ({
  clientName,
  onClose,
  embedded = false,
}) => {
  const {
    currentStep,
    totalSteps,
    isSaving,
    lastSavedAt,
    isDirty,
    saveProgress,
    enableAutoSave,
    setEnableAutoSave,
    goToPreviousStep,
    goToStep,
    completedSteps,
    reviewMode
  } = useKYCWizard();

  const [stepMenuOpen, setStepMenuOpen] = useState(false);
  const stepMenuRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!stepMenuOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (stepMenuRef.current && !stepMenuRef.current.contains(e.target as Node)) {
        setStepMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [stepMenuOpen]);

  // Render current step component - using switch to avoid instantiating all lazy components
  const renderStepContent = () => {
    const fallback = (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-kielis-gold border-t-transparent"></div>
      </div>
    );

    // TASK-373: 10-step wizard — DocumentUpload removed
    const renderStep = () => {
      switch (currentStep) {
        case 1:
          return <PersonalInfoStep key="step-1" />;
        case 2:
          return <ContactInfoStep key="step-2" />;
        case 3:
          return <EmploymentInfoStep key="step-3" />;
        case 4:
          return <PersonalBackgroundStep key="step-4" />;
        case 5:
          return <TaxInfoStep key="step-5" />;
        case 6:
          return <AdverseMediaStep key="step-6" />;
        case 7:
          return <BusinessActivitiesStep key="step-7" />;
        case 8:
          return <WealthSourceStep key="step-8" />;
        case 9:
          return <IncomeSourceStep key="step-9" />;
        case 10:
          return <ReviewSubmitStep key="step-10" />;
        default:
          return null;
      }
    };

    return (
      <Suspense fallback={fallback}>
        {renderStep()}
      </Suspense>
    );
  };

  // TASK-373: Embedded mode renders only the form content (no chrome)
  if (embedded) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-4">
          <div className="max-w-4xl mx-auto">
            {renderStepContent()}
          </div>
        </div>
        <div className="border-t border-gray-200 bg-white">
          <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
            <div>
              {currentStep > 1 && (
                <button
                  onClick={goToPreviousStep}
                  type="button"
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium flex items-center gap-1.5"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
              )}
            </div>
            <div className="relative" ref={stepMenuRef}>
              <button
                type="button"
                onClick={() => setStepMenuOpen((o) => !o)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <span className="text-gray-500">{currentStep}/{totalSteps}</span>
                <span className="max-w-[140px] truncate">{KYC_STEPS[currentStep - 1]?.name}</span>
                <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              </button>
              {stepMenuOpen && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 max-h-[320px] overflow-y-auto">
                  {KYC_STEPS.map((step, idx) => {
                    const stepNum = idx + 1;
                    const isActive = currentStep === stepNum;
                    const isCompleted = completedSteps.has(stepNum);
                    return (
                      <button
                        key={step.id}
                        type="button"
                        onClick={() => { goToStep(stepNum); setStepMenuOpen(false); }}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
                          isActive
                            ? 'bg-kielis-navy/5 text-kielis-navy font-medium'
                            : 'text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold ${
                          isActive
                            ? 'bg-kielis-navy text-white'
                            : isCompleted
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-200 text-gray-500'
                        }`}>
                          {isCompleted && !isActive ? <Check className="w-3 h-3" /> : stepNum}
                        </span>
                        <span className="truncate">{step.name}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div>
              {currentStep < totalSteps && (
                <button
                  type="submit"
                  form={`kyc-step-${currentStep}-form`}
                  className="px-4 py-2 bg-kielis-navy text-white rounded-lg hover:bg-opacity-90 text-sm font-medium flex items-center gap-1.5"
                >
                  Save & Continue
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-kielis-glacier">
      {/* Header - z-20 ensures it's above scrollable content */}
      <header className="bg-kielis-navy text-white shadow-lg relative z-20">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Title and client info */}
            <div className="flex items-center gap-6">
              <div>
                <h1 className="text-xl font-semibold">KYC Information</h1>
                <p className="text-sm text-gray-300 mt-1">Client: {clientName}</p>
              </div>
            </div>

            {/* Progress indicator */}
            <KYCProgressIndicator />

            {/* Actions */}
            <div className="flex items-center gap-4">
              {/* Auto-save toggle */}
              <div className="flex items-center gap-2 text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={enableAutoSave}
                    onChange={(e) => setEnableAutoSave(e.target.checked)}
                    className="rounded border-gray-300"
                  />
                  <span>Auto-save</span>
                </label>
              </div>

              {/* Save status */}
              {lastSavedAt && (
                <div className="flex items-center gap-1 text-sm text-gray-300">
                  <Clock className="w-3.5 h-3.5" />
                  <span>
                    Saved {format(lastSavedAt, 'HH:mm')}
                  </span>
                </div>
              )}

              {/* Manual save */}
              <button
                onClick={() => saveProgress(true)}
                disabled={isSaving || !isDirty}
                className="flex items-center gap-2 px-3 py-1.5 bg-white bg-opacity-10 rounded-md
                         hover:bg-opacity-20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                aria-label="Save progress"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" />
                    <span className="text-sm">Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span className="text-sm">Save</span>
                  </>
                )}
              </button>

              {/* Close button */}
              {onClose && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onClose();
                  }}
                  className="p-2 bg-white bg-opacity-10 hover:bg-opacity-20 rounded-full transition-colors cursor-pointer"
                  aria-label="Close wizard"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar navigation */}
        <aside className="w-80 bg-white border-r border-gray-200 shadow-sm">
          <KYCStepNavigation />
        </aside>

        {/* Form content */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Scrollable form area */}
          <div className="flex-1 overflow-y-auto p-8">
            <div className="max-w-4xl mx-auto">
              {renderStepContent()}
            </div>
          </div>

          {/* Fixed navigation footer */}
          <div className="border-t border-gray-200 bg-white shadow-lg">
            <div className="max-w-4xl mx-auto px-8 py-4 flex items-center justify-between">
              {/* Previous button */}
              <div>
                {currentStep > 1 && !(reviewMode && currentStep === 2) && (
                  <button
                    onClick={goToPreviousStep}
                    type="button"
                    className="px-6 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50
                             focus:outline-none focus:ring-2 focus:ring-kielis-gold transition-colors font-medium
                             flex items-center gap-2"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                )}
              </div>

              {/* Step indicator */}
              <div className="text-sm text-gray-500">
                Step {currentStep} of {totalSteps}
              </div>

              {/* Next button - triggers form submission */}
              <div>
                {currentStep < totalSteps && (
                  <button
                    type="submit"
                    form={`kyc-step-${currentStep}-form`}
                    className="px-6 py-2.5 bg-kielis-navy text-white rounded-lg hover:bg-opacity-90
                             focus:outline-none focus:ring-2 focus:ring-kielis-gold transition-colors font-medium
                             flex items-center gap-2"
                  >
                    {reviewMode ? 'Mark as Reviewed & Continue' : 'Save & Continue'}
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

const KYCWizard: React.FC<KYCWizardProps> = ({
  clientId,
  clientName,
  clientEmail,
  clientPhone,
  onClose,
  onComplete,
  externalPrefillData,
  reviewMode = false,
  embedded = false,
  reviewComments = [],
  isChangesRequested = false,
}) => {
  const [isLoadingPrefill, setIsLoadingPrefill] = useState(!externalPrefillData);
  const [prefillData, setPrefillData] = useState<any>(externalPrefillData || null);

  // Fetch prefill data on mount (TASK-133) - skip if external data provided
  useEffect(() => {
    // If external prefill data provided, skip fetch
    if (externalPrefillData) {
      console.log('[KYCWizard] Using external prefill data (staging review)');
      return;
    }

    const fetchKYCData = async () => {
      setIsLoadingPrefill(true);
      try {
        // TASK-210: First try to load existing KYC profile
        console.log('[KYCWizard] Attempting to load existing KYC profile...');
        const existingProfile = await kycService.loadDraft(clientId);

        if (existingProfile) {
          console.log('[KYCWizard] Loaded existing KYC profile for editing');
          setPrefillData(existingProfile);

          toast.success('KYC profile loaded for editing', {
            duration: 3000,
            position: 'bottom-right',
            icon: <CheckCircle2 className="w-5 h-5 text-blue-600" />
          });
        } else {
          // If no existing profile, try AI-extracted prefill data
          console.log('[KYCWizard] No existing profile, trying AI-extracted prefill data...');
          const data = await kycService.getPrefillData(clientId);

          if (data) {
            setPrefillData(data);

            // Show notification about prefilled data
            const fieldsCount = Object.keys(data._confidence || {}).length;
            const overallConfidence = data._extractionMetadata?.overallConfidence;

            toast.success(
              `${fieldsCount} fields pre-filled from uploaded documents`,
              {
                description: overallConfidence
                  ? `Extraction confidence: ${(overallConfidence * 100).toFixed(0)}%`
                  : undefined,
                duration: 5000,
                position: 'bottom-right',
                icon: <CheckCircle2 className="w-5 h-5 text-green-600" />
              }
            );
          }
        }
      } catch (error) {
        // Non-critical error - missing prefill data is OK for new clients
        console.log('No prefill data available:', error);
      } finally {
        setIsLoadingPrefill(false);
      }
    };

    fetchKYCData();
  }, [clientId, externalPrefillData]);

  // Merge prefill data with basic client info
  const initialData = React.useMemo(() => {
    // Start with basic client info
    const basicData = clientEmail || clientPhone ? {
      personalInfo: {
        firstName: clientName?.split(' ')[0] || '',
        lastName: clientName?.split(' ').slice(1).join(' ') || '',
        dateOfBirth: '',
        nationalities: []
      },
      contactInfo: {
        phonePrimary: clientPhone || '',
        emailPrimary: clientEmail || '',
        residentialAddress: {
          street: '',
          city: '',
          postalCode: '',
          country: ''
        }
      }
    } : {};

    // Merge with prefilled data (prefill data takes precedence)
    if (prefillData) {
      console.log('[KYCWizard] Creating initialData from prefillData.incomeSources:', prefillData.incomeSources);

      const initialDataObj = {
        ...basicData,
        ...prefillData,
        // Personal Info
        personalInfo: {
          ...basicData.personalInfo,
          ...prefillData.personalInfo
        },
        // Contact Info
        contactInfo: {
          ...basicData.contactInfo,
          ...prefillData.contactInfo,
          residentialAddress: {
            ...basicData.contactInfo?.residentialAddress,
            ...prefillData.contactInfo?.residentialAddress
          }
        },
        // Employment Info
        employmentInfo: prefillData.employmentInfo || {},
        // Tax Info
        taxInfo: prefillData.taxInfo || {},
        // Personal Background
        personalBackground: prefillData.personalBackground || {},
        // PEP Status
        pepStatus: prefillData.pepStatus || {},
        // Adverse Media (array)
        adverseMedia: prefillData.adverseMedia || [],
        // Business Activities (array)
        businessActivities: prefillData.businessActivities || [],
        // Wealth Sources (can be array or object with sources property)
        wealthSources: prefillData.wealthSources || [],
        // Income Sources (object with top-level fields + sources array) - Story 2.2.8
        incomeSources: prefillData.incomeSources || { sources: [] },
        // Documents (array)
        documents: prefillData.documents || []
      };

      console.log('[KYCWizard] initialData.incomeSources result:', initialDataObj.incomeSources);
      return initialDataObj;
    }

    return Object.keys(basicData).length > 0 ? basicData : undefined;
  }, [clientName, clientEmail, clientPhone, prefillData]);

  // Show loading screen while fetching prefill data
  if (isLoadingPrefill) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-kielis-glacier relative">
        {/* Close button for loading screen */}
        {onClose && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClose();
            }}
            className="absolute top-6 right-6 p-2 bg-white rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 z-50"
            aria-label="Close wizard"
          >
            <X className="w-5 h-5 text-gray-700" />
          </button>
        )}

        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full mx-4">
          <div className="flex flex-col items-center text-center">
            <Loader2 className="w-12 h-12 text-kielis-gold animate-spin mb-4" />
            <h2 className="text-xl font-semibold text-kielis-navy mb-2">
              Preparing KYC Form
            </h2>
            <p className="text-gray-600 mb-4">
              Loading pre-filled data from uploaded documents...
            </p>
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div className="bg-kielis-gold h-full animate-pulse" style={{ width: '60%' }} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <KYCWizardProvider
      clientId={clientId}
      initialData={initialData}
      onComplete={onComplete}
      reviewMode={reviewMode}
      embedded={embedded}
      reviewComments={reviewComments}
      isChangesRequested={isChangesRequested}
    >
      <KYCWizardContent clientName={clientName} onClose={onClose} embedded={embedded} />
    </KYCWizardProvider>
  );
};

export default KYCWizard;